import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Input, message, Modal, Space, Table, Tag } from "antd";
import { useNavigate } from "react-router-dom";
import firebase from "../../../../configs/FirebaseConfig";
import { FIRESTORE_AUDIENCES_TABLE } from "../../../../constants/FirestoreConstant";
import { useTranslation } from "react-i18next";
import { useFirestoreConnect } from "react-redux-firebase";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { repairSystemAudienceTriggers, SYSTEM_AUDIENCE_TRIGGERS } from "../../../../functions/audienceTriggers";

const db = firebase.firestore();

const SYSTEM_AUDIENCE_ID = "product_updates";

/**
 * Zet de systeemlijst klaar en trekt de triggers van een bestaande lijst recht. Dat herstel moet
 * hier gebeuren en niet alleen in de seed hieronder, omdat het document in productie allang
 * bestaat -- met de kapotte trigger erin.
 */
const ensureSystemAudienceExists = async () => {
  const ref = db.collection(FIRESTORE_AUDIENCES_TABLE).doc(SYSTEM_AUDIENCE_ID);
  const snap = await ref.get();

  if (!snap.exists) {
    await ref.set({
      id: SYSTEM_AUDIENCE_ID,
      name: "Product updates",
      description:
        "Krijgt notificaties bij product updates (nieuw, uitlopend, uit voorraad, terug op voorraad, preorder)",
      type: "system",
      readOnly: true,
      status: "active",
      channels: { email: true, inApp: true, sms: false },
      triggers: SYSTEM_AUDIENCE_TRIGGERS,
      members: [],
      createdAt: dayjs().unix(),
      createdBy: "system",
    });
    return;
  }

  const repaired = repairSystemAudienceTriggers(snap.data()?.triggers);
  if (repaired) {
    await ref.update({ triggers: repaired, updatedAt: dayjs().unix() });
  }
};

const AudiencesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Subscribe to audiences collection
  useFirestoreConnect([{ collection: FIRESTORE_AUDIENCES_TABLE }]);
  const audiences = useSelector((state) => state.firestoreReducer.ordered?.[FIRESTORE_AUDIENCES_TABLE]);
  const loading = audiences === undefined;
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    // Seed system audience once
    ensureSystemAudienceExists().catch((e) => console.error(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      {
        title: t("name"),
        dataIndex: "name",
        key: "name",
        render: (_, r) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              navigate(`/cms/customers/audiences/${r.id}`);
            }}
            style={{ color: "blue" }}
          >
            {r.name}
          </a>
        ),
      },
      {
        title: t("type"),
        dataIndex: "type",
        key: "type",
        render: (v) => (v === "system" ? <Tag color="geekblue">{t("system")}</Tag> : <Tag>{t("custom")}</Tag>),
      },
      {
        title: t("members"),
        key: "members",
        render: (_, r) => (Array.isArray(r.members) ? r.members.length : 0),
      },
      {
        title: t("status"),
        dataIndex: "status",
        key: "status",
        render: (v) => (v === "active" ? <Tag color="green">{t("active")}</Tag> : <Tag>{t("archived")}</Tag>),
      },
    ],
    [navigate, t],
  );

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      const ref = db.collection(FIRESTORE_AUDIENCES_TABLE).doc();
      await ref.set({
        id: ref.id,
        name: values.name,
        description: values.description || "",
        type: "custom",
        readOnly: false,
        status: "active",
        channels: { email: true, inApp: true, sms: false },
        triggers: [],
        members: [],
        createdAt: dayjs().unix(),
        createdBy: firebase.auth().currentUser?.uid || "unknown",
      });
      message.success(t("audience_created"));
      setIsCreateOpen(false);
      form.resetFields();
      // Realtime via subscription, geen expliciete fetch nodig
    } catch (e) {
      // already handled by antd if validation fails
      if (e) {
        console.log(e);
      }
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <h1>{t("audiences")}</h1>
      <Card>
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" onClick={() => setIsCreateOpen(true)}>
            {t("new_audience")}
          </Button>
        </Space>
        <Table rowKey={(r) => r.id} columns={columns} dataSource={audiences || []} loading={loading} />
      </Card>

      <Modal
        title={t("new_audience")}
        open={isCreateOpen}
        onCancel={() => setIsCreateOpen(false)}
        onOk={onCreate}
        okText={t("create")}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label={t("name")} name="name" rules={[{ required: true, message: t("enter_required_data") }]}>
            <Input placeholder="Bijv. Nieuwsbrief B2B" />
          </Form.Item>
          <Form.Item label={t("description")} name="description">
            <Input.TextArea rows={3} placeholder={t("optional")} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AudiencesList;
